import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  useGetFacialSyncSelectableDevicesAsync,
  useSyncFacialRecognitionDevicesAsync,
} from '../../../services/Devices/hooks/useMutation';
import { IFacialSyncDeviceSelectionProps } from '../../../services/Devices';

interface DeviceFacialSyncModalProps {
  open: boolean;
  onClose: () => void;
  onJobStarted: (jobId: number) => void;
  onError: (message: string) => void;
}

const BRAND_COLOR: Record<'ControlID' | 'Intelbras', string> = {
  ControlID: '#60a5fa',
  Intelbras: '#34d399',
};

const DeviceFacialSyncModal = ({ open, onClose, onJobStarted, onError }: DeviceFacialSyncModalProps) => {
  const [devices, setDevices] = useState<IFacialSyncDeviceSelectionProps[]>([]);
  const [selected, setSelected] = useState<IFacialSyncDeviceSelectionProps[]>([]);

  const { mutateAsync: fetchDevices, isLoading: isLoadingDevices } =
    useGetFacialSyncSelectableDevicesAsync();
  const { mutateAsync: triggerSync, isLoading: isSyncing } =
    useSyncFacialRecognitionDevicesAsync();

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    fetchDevices()
      .then(setDevices)
      .catch(() => onError('Não foi possível carregar a lista de dispositivos.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const groupedOptions = useMemo(
    () => [...devices].sort((a, b) => a.brand.localeCompare(b.brand) || a.name.localeCompare(b.name)),
    [devices],
  );

  const handleSync = async () => {
    if (selected.length === 0) return;
    try {
      const job = await triggerSync(selected.map((d) => d.id));
      onJobStarted(job.jobId);
      onClose();
    } catch (error: any) {
      onError(error?.message || 'Erro ao sincronizar dispositivos selecionados.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Sincronizar dispositivos específicos
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecione um ou mais dispositivos ControlID/Intelbras pra sincronizar o reconhecimento
          facial agora, sem esperar a sincronização agendada rodar em todos os dispositivos.
        </Typography>
        <Autocomplete
          multiple
          loading={isLoadingDevices}
          options={groupedOptions}
          groupBy={(option) => option.brand}
          getOptionLabel={(option) => option.name}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: option.isOnline ? 'success.main' : 'text.disabled',
                  flexShrink: 0,
                }}
              />
              {option.name}
            </Box>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.name}
                size="small"
                sx={{ bgcolor: `${BRAND_COLOR[option.brand]}22`, color: BRAND_COLOR[option.brand] }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Dispositivos" placeholder="Buscar dispositivo..." />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={selected.length === 0 || isSyncing}
          onClick={handleSync}
        >
          Sincronizar {selected.length > 0 ? `(${selected.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceFacialSyncModal;
